import React from 'react';
import { Phone, Mail, MapPin, Building, Calendar } from 'lucide-react';
import StatusBadge from '../shared/StatusBadge';
import { getCustomField } from '../../services/perfex-api';

/**
 * Lead Card Component
 * Displays a single lead in the lead list
 *
 * @param {Object} lead - Lead object from Perfex API
 * @param {boolean} isSelected - Whether this lead is currently selected
 * @param {function} onClick - Callback when lead is clicked
 */
const LeadCard = ({ lead, isSelected, onClick }) => {
    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';

        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    // Get service type from custom fields
    const getServiceInfo = () => {
        const materialType = getCustomField(lead, 'Material Type');
        const frequency = getCustomField(lead, 'Frequency');

        if (materialType || frequency) {
            return `${materialType || ''} ${frequency ? `• ${frequency}` : ''}`.trim();
        }
        return null;
    };

    const serviceInfo = getServiceInfo();

    return (
        <div
            onClick={onClick}
            className={`p-4 border-b border-gray-200 cursor-pointer transition-all hover:bg-gray-50 ${
                isSelected ? 'bg-green-50 border-l-4 border-l-green-600' : 'border-l-4 border-l-transparent'
            }`}
        >
            {/* Lead name and company */}
            <div className="mb-2">
                <h3 className={`font-semibold text-gray-800 ${isSelected ? 'text-green-700' : ''}`}>
                    {lead.name || 'Unnamed Lead'}
                </h3>
                {lead.company && (
                    <div className="flex items-center gap-1 text-sm text-gray-600 mt-1">
                        <Building size={14} />
                        <span>{lead.company}</span>
                    </div>
                )}
            </div>

            {/* Status badge */}
            <div className="mb-2">
                <StatusBadge
                    status={lead.status_name || 'Unknown'}
                    color={lead.color}
                    size="sm"
                />
            </div>

            {/* Contact information */}
            <div className="space-y-1 text-sm text-gray-600">
                {/* Phone */}
                {lead.phonenumber && (
                    <div className="flex items-center gap-2">
                        <Phone size={14} className="flex-shrink-0" />
                        <span>{lead.phonenumber}</span>
                    </div>
                )}

                {/* Email */}
                {lead.email && (
                    <div className="flex items-center gap-2">
                        <Mail size={14} className="flex-shrink-0" />
                        <span className="truncate">{lead.email}</span>
                    </div>
                )}

                {/* Address */}
                {lead.address && (
                    <div className="flex items-start gap-2">
                        <MapPin size={14} className="flex-shrink-0 mt-0.5" />
                        <span className="line-clamp-1">{lead.address}</span>
                    </div>
                )}

                {/* Service info from custom fields */}
                {serviceInfo && (
                    <div className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded mt-2">
                        {serviceInfo}
                    </div>
                )}

                {/* Last contact */}
                {lead.lastcontact && (
                    <div className="flex items-center gap-2 text-xs text-gray-500 mt-2">
                        <Calendar size={12} />
                        <span>Last contact: {formatDate(lead.lastcontact)}</span>
                    </div>
                )}
            </div>

            {/* Source badge */}
            {lead.source_name && (
                <div className="mt-2">
                    <span className="inline-block px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                        {lead.source_name}
                    </span>
                </div>
            )}
        </div>
    );
};

export default LeadCard;
